import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, UserCheck } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import { getAllUsers } from '@/utils/adminUsers';
import { trackLabel } from '@/components/SetupTrackPicker';

/* Admin Leads — reskinned to the Level 6 "Leads" pane mockup. A slim lead-top header
   ("Leads" + hint + a toggleable "N new" chip + Refresh), a filters card (qf-row of
   fields ending in Apply/Reset), and a card table of demo requests (Lead avatar/name/
   email, Company, Needs-help pill, truncated Topic, sortable Submitted date and a
   per-row status pill-select). All data wiring preserved verbatim — reads GET
   /auth/admin/leads, PATCHes status. Filters/sort run client-side over the live list.
   The mockup's Country filter + country flag and Convert-to-client action have no
   backing data/endpoint, so they are omitted (Search takes the Country slot). Scoped
   under `.adminx`. */


type Lead = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  companyName?: string;
  helpWith?: string;
  discussionTopic?: string;
  submittedAt?: string;
  status?: string;
  /** 'incorporate' | 'operate', captured by the public lead forms. */
  setupTrack?: string | null;
};

const STATUSES = ['new', 'contacted', 'scheduled', 'completed', 'dismissed'];
const STATUS_META: Record<string, { label: string }> = {
  new:       { label: 'New' },
  pending:   { label: 'New' },
  contacted: { label: 'Contacted' },
  scheduled: { label: 'Scheduled' },
  completed: { label: 'Completed' },
  dismissed: { label: 'Dismissed' },
};
/* Leads submitted before the forms asked have no `setupTrack`. Rather than a
   column of dashes, fall back to the client's own account: if the person later
   signed up and started a setup, `businessSetupData.type` is the same two-way
   split, and the cell says where the answer came from. */
const TRACKS = ['incorporate', 'operate'] as const;
type TrackFilter = '' | 'incorporate' | 'operate' | 'none';

const norm = (s?: string) => { const v = s === 'pending' ? 'new' : (s || 'new'); return STATUSES.includes(v) ? v : 'new'; };
const dayOf = (v?: string) => (v || '').slice(0, 10);
const fmtDate = (v?: string) => { if (!v) return '-'; try { return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return v; } };
const leadName = (l: Lead) => `${l.firstName || ''} ${l.lastName || ''}`.trim();
const initials = (l: Lead) => {
  const f = (l.firstName || '').trim();
  const ln = (l.lastName || '').trim();
  if (f || ln) return `${f[0] || ''}${ln[0] || ''}`.toUpperCase();
  const em = (l.email || '').trim();
  return em ? em.slice(0, 2).toUpperCase() : '?';
};

// Deterministic pastel avatar palette (bg|fg), indexed by name — mirrors the mockup's avStyle.
const AV_COLORS = ['#DBEAFE|#1D4ED8', '#EEF2F6|#475569', '#E9F7EF|#15803D', '#FEF6E7|#B45309', '#FDF2F8|#BE185D'];
const avStyle = (key: string) => {
  const sum = [...key].reduce((a, c) => a + c.charCodeAt(0), 0);
  const [background, color] = (AV_COLORS[sum % AV_COLORS.length] || AV_COLORS[0] || '#DBEAFE|#1D4ED8').split('|');
  return { background, color };
};

const CHEV = "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%2364748B' stroke-width='1.5'/%3E%3C/svg%3E\")";

const CSS = `
/* Page-specific only — tokens, buttons, cards and states come from adminShell.css. */
.adminx .new-chip{margin:0;font-size:12.5px;font-weight:600;color:var(--brand-d);background:var(--brand-soft);padding:6px 12px;border-radius:999px;border:1px solid transparent;font-family:inherit;cursor:pointer;transition:border-color .12s}
.adminx .new-chip:hover{border-color:var(--brand-line)}
.adminx .new-chip.on{border-color:var(--brand-d)}


.adminx .section-gap{margin-bottom:20px}
.adminx .lead-count{font-size:12px;color:#94A3B8;margin:0 2px 12px}

.adminx .qf-row{display:flex;align-items:flex-end;gap:14px;flex-wrap:wrap}
.adminx .qf-fields{display:flex;gap:14px;align-items:flex-end;flex-wrap:wrap}
.adminx .qf-actions{margin-left:auto;display:flex;gap:8px}
.adminx .fld{display:flex;flex-direction:column;gap:6px;min-width:0}
.adminx .fld label{font-size:11px;font-weight:600;letter-spacing:.6px;text-transform:uppercase;color:#64748B}
.adminx .fld input,.adminx .fld select{border:1px solid #E6E9ED;border-radius:9px;padding:9px 12px;font-size:13px;background:#fff;width:100%;font-family:inherit;color:#0F172A}
.adminx .fld select{-webkit-appearance:none;appearance:none;padding-right:30px;background-image:${CHEV};background-repeat:no-repeat;background-position:right 11px center}
.adminx .fld input:focus,.adminx .fld select:focus{border-color:var(--brand);outline:none;box-shadow:0 0 0 3px var(--brand-soft)}
.adminx .fld input::placeholder{color:#94A3B8}

.adminx .tblwrap{overflow-x:auto;border-radius:14px}.adminx .tbl th.sortable{cursor:pointer;user-select:none}
.adminx .tbl th.sortable:hover{color:#334155}
.adminx .tbl th .arrow{font-size:9px;margin-left:4px}
.adminx .cell-person{display:flex;align-items:center;gap:10px;min-width:0}
.adminx .cell-person .avatar{width:34px;height:34px;border-radius:50%;display:grid;place-items:center;font-size:12px;font-weight:600;flex:none}
.adminx .cell-person .who{min-width:0}
.adminx .cell-person b{display:block;font-size:13px;font-weight:600;color:#0F172A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.adminx .cell-person a{display:block;font-size:11.5px;color:#94A3B8;text-decoration:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:240px}
.adminx .cell-person a:hover{color:var(--brand);text-decoration:underline}
.adminx .cell-person span.dash{font-size:11.5px;color:#CBD5E1}
.adminx .company{color:#334155;font-weight:500;white-space:nowrap}
.adminx .muted{color:#CBD5E1}

.adminx .pill{display:inline-flex;align-items:center;font-size:11.5px;font-weight:600;padding:4px 10px;border-radius:999px;white-space:nowrap;max-width:200px;overflow:hidden;text-overflow:ellipsis}
.adminx .pill.plain{background:#EFF1F4;color:#64748B}
.adminx .pill.trk{gap:5px}
.adminx .pill.trk-incorporate{background:var(--brand-soft);color:var(--brand-d)}
.adminx .pill.trk-operate{background:#E9F7EF;color:#15803D}
/* Read off the client's account rather than answered on the form — same colour,
   quieter, and the note says where it came from. */
.adminx .pill.trk.derived{background:#EFF1F4;color:#64748B}
.adminx .pill.trk em{font-style:normal;font-size:9.5px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;opacity:.7}
.adminx td.topic-cell{max-width:280px}
.adminx td.topic-cell span{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#334155}
.adminx .submitted{color:#94A3B8;font-weight:500;white-space:nowrap}

.adminx .status-select{-webkit-appearance:none;appearance:none;border:1px solid #E6E9ED;border-radius:999px;padding:5px 26px 5px 12px;font:inherit;font-size:12px;font-weight:600;cursor:pointer;background-image:${CHEV};background-repeat:no-repeat;background-position:right 10px center;transition:.14s}
.adminx .status-select:focus{outline:none;box-shadow:0 0 0 3px var(--brand-soft)}
.adminx .status-select:disabled{opacity:.6;cursor:default}
.adminx .status-select option{color:#0F172A;background:#fff}
.adminx .status-select[data-s="New"]{color:#475569;background-color:#EEF2F6;border-color:transparent}
.adminx .status-select[data-s="Contacted"],.adminx .status-select[data-s="Scheduled"]{color:var(--brand-d);background-color:var(--brand-soft);border-color:transparent}
.adminx .status-select[data-s="Completed"]{color:#15803D;background-color:#E9F7EF;border-color:transparent}
.adminx .status-select[data-s="Dismissed"]{color:#DC2626;background-color:#FEF2F2;border-color:transparent}/* Column-centred: the shared .empty only sets text-align, which leaves the icon
   box and the max-width paragraph — both blocks — pinned to the left edge. */
.adminx .ld-empty{display:flex;flex-direction:column;align-items:center;gap:8px}
.adminx .ld-empty .ico{width:44px;height:44px;border-radius:12px;background:#EEF2F6;color:#94A3B8;display:grid;place-items:center;flex:none}
.adminx .ld-empty .ico svg{width:20px;height:20px;margin:0}
.adminx .ld-empty b{font-size:14px;color:#0F172A;margin:0}
.adminx .ld-empty p{font-size:12.5px;color:#64748B;max-width:360px}
.adminx .ld-empty .btn{margin-top:4px}

.adminx .loading{display:flex;justify-content:center;padding:70px 0}
.adminx .loader{width:30px;height:30px;border:3px solid var(--brand-soft);border-top-color:var(--brand);border-radius:50%;animation:axspin .8s linear infinite}

@media(max-width:860px){
  .adminx .qf-actions{margin-left:0}
  .adminx .tbl th.hide-sm,.adminx .tbl td.hide-sm{display:none}
}
`;

export default function AdminLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  // Draft filter inputs (committed to `applied` on Apply).
  const [dq, setDq] = useState('');
  const [dStatus, setDStatus] = useState('');
  const [dNeeds, setDNeeds] = useState('');
  const [dTrack, setDTrack] = useState<TrackFilter>('');
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo] = useState('');
  const [applied, setApplied] = useState({ q: '', status: '', needs: '', track: '' as TrackFilter, from: '', to: '' });
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/leads`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load leads');
      setLeads(json.demoRequests || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /* The client list is a shared, cached fetch, so this costs nothing on a warm
     admin session. It only supplies the fallback track for older leads. */
  const [acctTrack, setAcctTrack] = useState<Record<string, string>>({});
  useEffect(() => {
    let live = true;
    getAllUsers().then(list => {
      if (!live) return;
      const map: Record<string, string> = {};
      (Array.isArray(list) ? list : []).forEach((u: any) => {
        const email = String(u?.email || '').trim().toLowerCase();
        const type = u?.businessSetupData?.type;
        if (email && (type === 'incorporate' || type === 'operate')) map[email] = type;
      });
      setAcctTrack(map);
    }).catch(() => { /* the column just stays empty */ });
    return () => { live = false; };
  }, []);

  /** The track for a lead, and whether we read it off their account. */
  const trackOf = (l: Lead): { value: string; derived: boolean } => {
    const own = l.setupTrack;
    if (own === 'incorporate' || own === 'operate') return { value: own, derived: false };
    const fromAccount = acctTrack[String(l.email || '').trim().toLowerCase()];
    return fromAccount ? { value: fromAccount, derived: true } : { value: '', derived: false };
  };

  const setStatus = async (lead: Lead, status: string) => {
    setSavingId(lead.id);
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status } : l)); // optimistic
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/leads/${lead.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
    } catch {
      load(); // revert to server truth on failure
    } finally {
      setSavingId(null);
    }
  };

  const applyFilters = () => setApplied({ q: dq, status: dStatus, needs: dNeeds, track: dTrack, from: dFrom, to: dTo });
  const resetFilters = () => {
    setDq(''); setDStatus(''); setDNeeds(''); setDTrack(''); setDFrom(''); setDTo('');
    setApplied({ q: '', status: '', needs: '', track: '', from: '', to: '' });
  };
  const toggleNew = () => {
    const next = applied.status === 'new' ? '' : 'new';
    setDStatus(next);
    setApplied(a => ({ ...a, status: next }));
  };

  // "Needs help with" options, derived from the live data so the filter always matches reality.
  const needsOptions = useMemo(() => {
    const set = new Set<string>();
    leads.forEach(l => { const v = (l.helpWith || '').trim(); if (v) set.add(v); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  const filtered = useMemo(() => {
    const s = applied.q.trim().toLowerCase();
    return leads
      .filter(l => {
        if (applied.status && norm(l.status) !== applied.status) return false;
        if (applied.needs && (l.helpWith || '') !== applied.needs) return false;
        if (applied.track) {
          const t = trackOf(l).value;
          if (applied.track === 'none' ? !!t : t !== applied.track) return false;
        }
        const d = dayOf(l.submittedAt);
        if (applied.from && d && d < applied.from) return false;
        if (applied.to && d && d > applied.to) return false;
        if (s && !`${l.firstName || ''} ${l.lastName || ''} ${l.email || ''} ${l.companyName || ''} ${l.discussionTopic || ''} ${l.helpWith || ''} ${trackOf(l).value}`.toLowerCase().includes(s)) return false;
        return true;
      })
      .sort((a, b) => {
        const da = a.submittedAt || ''; const db = b.submittedAt || '';
        const r = da < db ? -1 : (da > db ? 1 : 0);
        return sortDir === 'asc' ? r : -r;
      });
  }, [leads, applied, sortDir, acctTrack]);

  const newCount = leads.filter(l => norm(l.status) === 'new').length;

  return (
    <AdminLayout
      section="Overview"
      title="Leads"
      icon={UserCheck}
      subtitle="Demo requests from the Conncct site: filter and follow up below."
      actions={<>
        <button
          className={`new-chip${applied.status === 'new' ? ' on' : ''}`}
          onClick={toggleNew}
          title="Show only new leads"
        >
          {newCount} new
        </button>
        <button className="btn" onClick={load} disabled={loading}>
          <RefreshCw className={loading ? 'spin' : ''} /> Refresh
        </button>
      </>}
    >
      <style>{CSS}</style>

      {error && <div className="errbar">{error}</div>}

      <div className="card section-gap">
        <div className="card-b">
          <div className="qf-row">
            <div className="qf-fields">
              <div className="fld" style={{ minWidth: 220 }}>
                <label htmlFor="lfSearch">Search</label>
                <input
                  id="lfSearch"
                  value={dq}
                  onChange={e => setDq(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
                  placeholder="Name, email, company or topic…"
                />
              </div>
              <div className="fld" style={{ minWidth: 160 }}>
                <label htmlFor="lfStatus">Status</label>
                <select id="lfStatus" value={dStatus} onChange={e => setDStatus(e.target.value)}>
                  <option value="">All statuses</option>
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s]?.label ?? s}</option>)}
                </select>
              </div>
              <div className="fld" style={{ minWidth: 180 }}>
                <label htmlFor="lfNeeds">Needs help with</label>
                <select id="lfNeeds" value={dNeeds} onChange={e => setDNeeds(e.target.value)}>
                  <option value="">All categories</option>
                  {needsOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="fld" style={{ minWidth: 160 }}>
                <label htmlFor="lfTrack">Track</label>
                <select id="lfTrack" value={dTrack} onChange={e => setDTrack(e.target.value as TrackFilter)}>
                  <option value="">Any track</option>
                  {TRACKS.map(t => <option key={t} value={t}>{trackLabel(t)}</option>)}
                  <option value="none">Not known</option>
                </select>
              </div>
              <div className="fld">
                <label htmlFor="lfFrom">Submitted from</label>
                <input id="lfFrom" type="date" value={dFrom} onChange={e => setDFrom(e.target.value)} />
              </div>
              <div className="fld">
                <label htmlFor="lfTo">Submitted to</label>
                <input id="lfTo" type="date" value={dTo} onChange={e => setDTo(e.target.value)} />
              </div>
            </div>
            <div className="qf-actions">
              <button className="btn btn-pri" onClick={applyFilters}>Apply filters</button>
              <button className="btn" onClick={resetFilters} title="Reset filters" aria-label="Reset filters">↺</button>
            </div>
          </div>
        </div>
      </div>

      {!loading && !error && (
        <div className="lead-count">Showing {filtered.length} of {leads.length} leads</div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading"><div className="loader" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty ld-empty">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.2 3.4-5 6.5-5s5.7 1.8 6.5 5" />
              </svg>
            </span>
            <b>{leads.length === 0 ? 'No demo requests yet' : 'No leads match your search or filters'}</b>
            <p>
              {leads.length === 0
                ? 'New demo requests submitted from the Conncct site will appear here the moment they land.'
                : 'Try a different name, company or topic, or widen your filters.'}
            </p>
            {leads.length > 0 && <button className="btn" onClick={resetFilters}>Clear search &amp; filters</button>}
          </div>
        ) : (
          <div className="tblwrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Company</th>
                  <th>Needs help with</th>
                  <th>Track</th>
                  <th className="hide-sm">Topic</th>
                  <th
                    className="sortable hide-sm"
                    onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
                  >
                    Submitted<span className="arrow">{sortDir === 'asc' ? '▲' : '▼'}</span>
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(l => {
                  const status = norm(l.status);
                  const name = leadName(l);
                  return (
                    <tr key={l.id}>
                      <td>
                        <div className="cell-person">
                          <span className="avatar" style={avStyle(name || l.email || '?')}>{initials(l)}</span>
                          <div className="who">
                            <b>{name || 'Unnamed lead'}</b>
                            {l.email ? <a href={`mailto:${l.email}`}>{l.email}</a> : <span className="dash">-</span>}
                          </div>
                        </div>
                      </td>
                      <td>{l.companyName ? <span className="company">{l.companyName}</span> : <span className="muted">-</span>}</td>
                      <td>{l.helpWith ? <span className="pill plain" title={l.helpWith}>{l.helpWith}</span> : <span className="muted">-</span>}</td>
                      <td>
                        {(() => {
                          const { value, derived } = trackOf(l);
                          if (!value) return <span className="muted" title="This form did not ask">-</span>;
                          return (
                            <span
                              className={`pill trk trk-${value}${derived ? ' derived' : ''}`}
                              title={derived
                                ? `${trackLabel(value)} — from their client account, not the lead form`
                                : `${trackLabel(value)} — answered on the form`}
                            >
                              {trackLabel(value)}{derived && <em>from account</em>}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="topic-cell hide-sm"><span title={l.discussionTopic || ''}>{l.discussionTopic || '-'}</span></td>
                      <td className="hide-sm"><span className="submitted">{fmtDate(l.submittedAt)}</span></td>
                      <td>
                        <select
                          className="status-select"
                          data-s={STATUS_META[status]?.label ?? status}
                          value={status}
                          disabled={savingId === l.id}
                          onChange={e => setStatus(l, e.target.value)}
                          aria-label={`Status for ${name || 'lead'}`}
                        >
                          {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s]?.label ?? s}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
